/**
 * AdminDataScopeBanner
 *
 * Persistent, non-dismissable one-line banner answering "whose data is this
 * page showing/affecting" — the blind spot documented in
 * .planning/admin-env-indicator-guardrails-plan.md. Mounted once in
 * AdminPortalPage between the context bar and the tab content, keyed on the
 * active tab id.
 *
 * Two independent signals combine to decide what (if anything) to show:
 * - getTabDataSource(tabId) — presentation-layer, static: does this tab's
 *   data come from a route that CAN redirect to production
 *   (get_analytics_db_session()), or is it always environment-local?
 * - adminEnvironment (state/userState.js) — backend-authoritative, fed from
 *   GET /api/v1/admin/me/permissions: which environment this request is
 *   served from, and whether that redirect is actually active right now
 *   (analytics_reads_production is true only on staging today).
 */
import { getTabDataSource, adminEnvironment } from '../../state/userState.js';
import { IconInfo } from '../shared/Icons.jsx';

const STAGING_VALUES = ['staging', 'stag'];

export function AdminDataScopeBanner({ tabId }) {
  const { environment, analytics_reads_production: analyticsReadsProduction } =
    adminEnvironment.value;
  const isStagingEnv = STAGING_VALUES.includes((environment || '').toLowerCase());
  const dataSource = getTabDataSource(tabId);

  // prod-live page whose redirect is actually active (staging today) —
  // whatever this tab shows/creates is real production data, not staging's.
  if (dataSource === 'prod-live' && analyticsReadsProduction) {
    return (
      <div className="admin-data-scope-banner admin-data-scope-banner--prod-live">
        <IconInfo size={14} />
        <span>
          Showing live production data — this is real customer activity, not staging data. Any
          action you take here (including annotations) writes to production.
        </span>
      </div>
    );
  }

  // env-local page on staging — unambiguous reassurance that nothing here
  // touches production.
  if (dataSource === 'env-local' && isStagingEnv) {
    return (
      <div className="admin-data-scope-banner admin-data-scope-banner--staging">
        <span>This is the Staging environment — changes here don't affect production.</span>
      </div>
    );
  }

  // Everything else (env-local on production; prod-live tab on production,
  // where the redirect resolves back to production's own DB anyway) is
  // unambiguous — no banner needed.
  return null;
}
